import type { CourseModule } from '@/types/lesson';

/**
 * Модуль 5: Удалённые репозитории
 */
const module5: CourseModule = {
  id: 'module-5',
  title: 'Удалённые репозитории',
  slug: 'remote-workflows',
  order: 5,
  lessons: [
    {
      id: '5-1',
      moduleId: 'module-5',
      title: 'Клонирование и Fetch',
      slug: 'cloning-fetching',
      order: 1,
      theory: `# Клонирование и Fetch

Работа с удалёнными репозиториями начинается с клонирования.

## Клонирование репозитория

\`\`\`bash
# Клонировать по HTTPS
git clone https://github.com/user/repo.git

# Клонировать по SSH
git clone git@github.com:user/repo.git

# Клонировать в определённую директорию
git clone https://github.com/user/repo.git my-project
\`\`\`

## Что происходит при клонировании?

1. Создаётся локальная копия репозитория
2. Добавляется remote с именем "origin"
3. Загружаются все ветки и история
4. Создаётся локальная ветка, отслеживающая main/master

## Просмотр удалённых репозиториев

\`\`\`bash
# Список удалённых репозиториев
git remote -v

# Информация об удалённом репозитории
git remote show origin
\`\`\`

## Git Fetch

Fetch загружает изменения без слияния:

\`\`\`bash
# Загрузить изменения из origin
git fetch origin

# Загрузить из всех удалённых репозиториев
git fetch --all

# Загрузить конкретную ветку
git fetch origin main
\`\`\`

## Fetch vs Pull

- **Fetch**: Загружает изменения, не меняя локальные ветки
- **Pull**: Загружает и сразу сливает (fetch + merge)
`,
      steps: [
        {
          id: 1,
          title: 'Просмотр удалённых репозиториев',
          description: 'Проверьте настроенные удалённые репозитории.',
          expectedCommand: 'git remote -v',
          hint: {
            level1: 'Посмотрите список удалённых репозиториев.',
            level2: 'Используйте git remote -v.',
            level3: 'Введите: git remote -v',
          },
        },
      ],
      keyInsight: {
        main: 'Fetch безопаснее pull — вы можете просмотреть изменения перед слиянием.',
        example:
          'git fetch origin загружает все изменения с сервера, но не меняет ваши локальные ветки. Затем git log origin/main покажет, что нового, и вы решите, когда и как сливать.',
        warning:
          'git pull выполняет fetch + merge автоматически. Если есть конфликты, они возникнут неожиданно. Лучше сначала fetch, потом merge или rebase.',
        tip: 'Используйте git fetch --prune для удаления ссылок на удалённые ветки, которые больше не существуют на сервере.',
        additional: [
          'git fetch origin main загружает только указанную ветку.',
          'git fetch --all загружает из всех удалённых репозиториев.',
          'После fetch изменения доступны как origin/branch-name.',
        ],
      },
      estimatedTime: 15,
      quizQuestions: [
        {
          id: '5-1-q1',
          question: 'В чём разница между git fetch и git pull?',
          options: [
            {
              id: 'a',
              text: 'Нет разницы',
              isCorrect: false,
              explanation: 'Неправильно. Это разные команды.',
            },
            {
              id: 'b',
              text: 'Fetch загружает, pull загружает и сливает',
              isCorrect: true,
              explanation: 'Правильно! pull = fetch + merge.',
            },
            {
              id: 'c',
              text: 'Fetch быстрее, pull медленнее',
              isCorrect: false,
              explanation: 'Неправильно. Скорость не главное отличие.',
            },
            {
              id: 'd',
              text: 'Fetch для веток, pull для файлов',
              isCorrect: false,
              explanation: 'Неправильно. Обе команды работают с ветками.',
            },
          ],
          explanation:
            'git fetch загружает изменения с удалённого сервера, но не изменяет локальные ветки. git pull = git fetch + git merge — загружает и сразу сливает изменения.',
          difficulty: 'easy',
        },
        {
          id: '5-1-q2',
          question: 'Как посмотреть изменения в удалённой ветке после fetch?',
          options: [
            {
              id: 'a',
              text: 'git diff origin/main',
              isCorrect: false,
              explanation: 'Неправильно. Это сравнивает, а не показывает историю.',
            },
            {
              id: 'b',
              text: 'git log origin/main',
              isCorrect: true,
              explanation: 'Правильно! origin/main — это удалённая ветка после fetch.',
            },
            {
              id: 'c',
              text: 'git show origin/main',
              isCorrect: false,
              explanation: 'Неправильно. show показывает один коммит.',
            },
            {
              id: 'd',
              text: 'git list origin/main',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'После git fetch изменения доступны как origin/branch-name. git log origin/main покажет историю удалённой ветки.',
          difficulty: 'easy',
        },
        {
          id: '5-1-q3',
          question: 'Как удалить ссылки на удалённые ветки, которых больше нет на сервере?',
          options: [
            {
              id: 'a',
              text: 'git fetch --clean',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
            {
              id: 'b',
              text: 'git fetch --prune',
              isCorrect: true,
              explanation: 'Правильно! --prune удаляет устаревшие ссылки.',
            },
            {
              id: 'c',
              text: 'git fetch --delete',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
            {
              id: 'd',
              text: 'git fetch --remove',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
          ],
          explanation:
            'git fetch --prune (или git remote prune origin) удаляет локальные ссылки на удалённые ветки, которые были удалены с сервера.',
          difficulty: 'medium',
        },
        {
          id: '5-1-q4',
          question: 'Что происходит при git clone?',
          options: [
            {
              id: 'a',
              text: 'Только скачивает файлы',
              isCorrect: false,
              explanation: 'Неправильно. Clone делает больше.',
            },
            {
              id: 'b',
              text: 'Создаёт локальную копию, добавляет remote, загружает историю',
              isCorrect: true,
              explanation: 'Правильно! Clone — полная инициализация.',
            },
            {
              id: 'c',
              text: 'Только создаёт .git директорию',
              isCorrect: false,
              explanation: 'Неправильно. Это делает git init.',
            },
            {
              id: 'd',
              text: 'Только добавляет remote',
              isCorrect: false,
              explanation: 'Неправильно. Clone делает больше.',
            },
          ],
          explanation:
            'git clone создаёт локальную копию репозитория: инициализирует .git, добавляет remote origin, загружает все ветки и историю, создаёт локальную ветку, отслеживающую main/master.',
          difficulty: 'easy',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git clone',
          url: 'https://git-scm.com/docs/git-clone',
          type: 'documentation',
          description: 'Официальная документация о команде clone',
        },
        {
          title: 'Git Documentation: git fetch',
          url: 'https://git-scm.com/docs/git-fetch',
          type: 'documentation',
          description: 'Официальная документация о команде fetch',
        },
        {
          title: 'Fetching vs Pulling',
          url: 'https://www.atlassian.com/git/tutorials/syncing',
          type: 'article',
          description: 'Статья о синхронизации репозиториев от Atlassian',
        },
      ],
    },
    {
      id: '5-2',
      moduleId: 'module-5',
      title: 'Push и Pull',
      slug: 'push-pull',
      order: 2,
      theory: `# Push и Pull

Основные команды для синхронизации с удалённым репозиторием.

## Git Push

Отправка локальных изменений на сервер:

\`\`\`bash
# Отправить текущую ветку
git push origin main

# Отправить и установить отслеживание
git push -u origin feature-branch

# Отправить все ветки
git push --all origin

# Принудительная отправка (осторожно!)
git push --force origin main
\`\`\`

## Git Pull

Получение и слияние изменений:

\`\`\`bash
# Получить и слить текущую ветку
git pull origin main

# Pull с rebase вместо merge
git pull --rebase origin main
\`\`\`

## Конфликты при Pull

Если есть локальные изменения:

\`\`\`bash
# 1. Спрятать изменения
git stash

# 2. Выполнить pull
git pull

# 3. Вернуть изменения
git stash pop
\`\`\`

## Отслеживание веток

\`\`\`bash
# Установить отслеживание при push
git push -u origin feature-branch

# Установить отслеживание для существующей ветки
git branch --set-upstream-to=origin/feature-branch feature-branch
\`\`\`
`,
      steps: [
        {
          id: 1,
          title: 'Проверка статуса',
          description: 'Проверьте состояние репозитория перед синхронизацией.',
          expectedCommand: 'git status',
          hint: {
            level1: 'Проверьте текущее состояние.',
            level2: 'Используйте git status.',
            level3: 'Введите: git status',
          },
        },
      ],
      keyInsight: {
        main: 'Всегда делайте pull перед push, чтобы избежать конфликтов.',
        example:
          'Вы и коллега изменили один файл. Вы пытаетесь push — Git отклоняет. Выполняете git pull, разрешаете конфликт, затем push. Без pull ваши изменения не попадут на сервер.',
        warning:
          'git push --force перезаписывает удалённую историю! Используйте только если точно понимаете последствия. Лучше --force-with-lease для безопасности.',
        tip: 'git push -u origin branch устанавливает отслеживание. После этого можно использовать просто git push и git pull без указания remote и ветки.',
        additional: [
          'git push --all отправляет все ветки.',
          'git push --tags отправляет все теги.',
          'git push --force-with-lease безопаснее --force.',
        ],
      },
      estimatedTime: 15,
      quizQuestions: [
        {
          id: '5-2-q1',
          question:
            'Какая команда отправляет локальную ветку на удалённый сервер и устанавливает отслеживание?',
          options: [
            {
              id: 'a',
              text: 'git push origin branch',
              isCorrect: false,
              explanation:
                'Неправильно. Эта команда отправляет ветку, но не устанавливает отслеживание.',
            },
            {
              id: 'b',
              text: 'git push -u origin branch',
              isCorrect: true,
              explanation:
                'Правильно! Флаг -u (или --set-upstream) устанавливает отслеживание ветки.',
            },
            {
              id: 'c',
              text: 'git push --track origin branch',
              isCorrect: false,
              explanation: 'Неправильно. Флага --track нет в git push.',
            },
            {
              id: 'd',
              text: 'git push --follow origin branch',
              isCorrect: false,
              explanation: 'Неправильно. Флага --follow нет в git push.',
            },
          ],
          explanation:
            'git push -u origin branch отправляет ветку и устанавливает upstream отслеживание. После этого можно использовать git push и git pull без указания remote и ветки.',
          difficulty: 'easy',
        },
        {
          id: '5-2-q2',
          question: 'В чём разница между git pull и git pull --rebase?',
          options: [
            {
              id: 'a',
              text: 'Нет разницы',
              isCorrect: false,
              explanation: 'Неправильно. Разница есть в том, как применяются локальные изменения.',
            },
            {
              id: 'b',
              text: 'git pull создаёт merge commit, git pull --rebase перемещает коммиты',
              isCorrect: true,
              explanation:
                'Правильно! --rebase применяет локальные коммиты поверх удалённых вместо создания merge commit.',
            },
            {
              id: 'c',
              text: 'git pull загружает все ветки, git pull --rebase только текущую',
              isCorrect: false,
              explanation: 'Неправильно. Обе команды работают с текущей веткой.',
            },
            {
              id: 'd',
              text: 'git pull быстрее, чем git pull --rebase',
              isCorrect: false,
              explanation: 'Неправильно. Скорость примерно одинакова.',
            },
          ],
          explanation:
            'git pull = git fetch + git merge. git pull --rebase = git fetch + git rebase. Rebase создаёт более чистую историю без лишних merge commits.',
          difficulty: 'medium',
        },
        {
          id: '5-2-q3',
          question: 'Какая команда безопаснее: git push --force или git push --force-with-lease?',
          options: [
            {
              id: 'a',
              text: 'git push --force, потому что она гарантированно перезаписывает',
              isCorrect: false,
              explanation: 'Неправильно. Гарантированная перезапись может быть опасной.',
            },
            {
              id: 'b',
              text: 'git push --force-with-lease, потому что она проверяет изменения других',
              isCorrect: true,
              explanation:
                'Правильно! --force-with-lease отклонит push, если удалённая ветка изменилась другими.',
            },
            {
              id: 'c',
              text: 'Они одинаковые',
              isCorrect: false,
              explanation: 'Неправильно. --force-with-lease добавляет проверку безопасности.',
            },
            {
              id: 'd',
              text: 'Обе команды безопасны',
              isCorrect: false,
              explanation:
                'Неправильно. Обе команды перезаписывают историю, но --force-with-lease безопаснее.',
            },
          ],
          explanation:
            'git push --force-with-lease проверяет, что удалённая ветка находится в ожидаемом состоянии перед перезаписью. Это предотвращает случайную перезапись чужих изменений.',
          difficulty: 'medium',
        },
        {
          id: '5-2-q4',
          question:
            'Что произойдёт, если выполнить git push без предварительного git pull, когда удалённая ветка опережает локальную?',
          options: [
            {
              id: 'a',
              text: 'Git автоматически выполнит merge',
              isCorrect: false,
              explanation: 'Неправильно. Git не выполняет автоматический merge при push.',
            },
            {
              id: 'b',
              text: 'Git отклонит push с сообщением о non-fast-forward',
              isCorrect: true,
              explanation: 'Правильно! Git защитит от потери удалённых изменений.',
            },
            {
              id: 'c',
              text: 'Git перезапишет удалённую ветку',
              isCorrect: false,
              explanation: 'Неправильно. Для перезаписи нужен --force.',
            },
            {
              id: 'd',
              text: 'Git создаст новую ветку',
              isCorrect: false,
              explanation: 'Неправильно. Git не создаёт ветки автоматически.',
            },
          ],
          explanation:
            'Если удалённая ветка содержит коммиты, которых нет локально, Git отклонит push. Это защита от потери чужих изменений. Нужно сначала выполнить git pull.',
          difficulty: 'easy',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git push',
          url: 'https://git-scm.com/docs/git-push',
          type: 'documentation',
          description: 'Официальная документация о команде push',
        },
        {
          title: 'Git Documentation: git pull',
          url: 'https://git-scm.com/docs/git-pull',
          type: 'documentation',
          description: 'Официальная документация о команде pull',
        },
        {
          title: 'Force Push Safely',
          url: 'https://www.atlassian.com/git/tutorials/force-pushing',
          type: 'article',
          description: 'Безопасное использование force push от Atlassian',
        },
      ],
    },
    {
      id: '5-3',
      moduleId: 'module-5',
      title: 'Удалённые ветки',
      slug: 'remote-branches',
      order: 3,
      theory: `# Удалённые ветки

Понимание работы с удалёнными ветками критически важно для командной разработки.

## Соглашение об именах

- \`main\` / \`master\` — основная ветка
- \`origin/main\` — удалённая ветка на сервере origin
- \`feature/*\` — ветки функций
- \`hotfix/*\` — ветки срочных исправлений

## Просмотр удалённых веток

\`\`\`bash
# Все ветки (локальные и удалённые)
git branch -a

# Только удалённые ветки
git branch -r
\`\`\`

## Создание локальной ветки из удалённой

\`\`\`bash
# Создать локальную ветку, отслеживающую удалённую
git checkout -b feature-branch origin/feature-branch

# Или короче (если имена совпадают)
git checkout --track origin/feature-branch
\`\`\`

## Удаление удалённых веток

\`\`\`bash
# Удалить ветку на сервере
git push origin --delete feature-branch

# Удалить отслеживающую ссылку на удалённую ветку
git branch -d -r origin/feature-branch

# Очистить устаревшие ссылки
git fetch --prune
\`\`\`

## Отслеживание веток

\`\`\`bash
# Посмотреть, какие ветки отслеживаются
git branch -vv
\`\`\`
`,
      steps: [
        {
          id: 1,
          title: 'Список всех веток',
          description: 'Посмотрите все ветки, включая удалённые.',
          expectedCommand: 'git branch -a',
          hint: {
            level1: 'Посмотрите все ветки.',
            level2: 'Используйте git branch -a.',
            level3: 'Введите: git branch -a',
          },
        },
      ],
      keyInsight: {
        main: 'Удалённые ветки — это только для чтения. Для работы создайте локальную отслеживающую ветку.',
        example:
          'origin/feature-login — это удалённая ветка. Вы не можете коммитить в неё напрямую. Создайте локальную: git checkout -b feature-login origin/feature-login.',
        warning:
          'Удалённые ветки обновляются только при fetch или pull. Если коллега запушил изменения, вы не увидите их до следующего fetch.',
        tip: 'git branch -vv показывает, какие локальные ветки отслеживают какие удалённые. Это полезно для понимания структуры репозитория.',
        additional: [
          'git fetch --prune удаляет ссылки на удалённые ветки.',
          'git branch -r показывает только удалённые ветки.',
          'git checkout --track origin/branch создаёт отслеживающую ветку.',
        ],
      },
      estimatedTime: 15,
      quizQuestions: [
        {
          id: '5-3-q1',
          question: 'Что означает запись origin/main?',
          options: [
            {
              id: 'a',
              text: 'Локальную ветку main в репозитории origin',
              isCorrect: false,
              explanation: 'Неправильно. origin/main — это удалённая ветка.',
            },
            {
              id: 'b',
              text: 'Удалённую ветку main на сервере origin',
              isCorrect: true,
              explanation:
                'Правильно! origin/main — это ссылка на ветку main в удалённом репозитории origin.',
            },
            {
              id: 'c',
              text: 'Тег main в репозитории origin',
              isCorrect: false,
              explanation: 'Неправильно. Теги обозначаются иначе.',
            },
            {
              id: 'd',
              text: 'Ветку origin в репозитории main',
              isCorrect: false,
              explanation: 'Неправильно. Порядок обратный: remote/branch.',
            },
          ],
          explanation:
            'Формат remote/branch (например, origin/main) обозначает удалённую ветку. origin — это имя remote, main — имя ветки на сервере.',
          difficulty: 'easy',
        },
        {
          id: '5-3-q2',
          question: 'Как создать локальную ветку, отслеживающую удалённую origin/feature?',
          options: [
            {
              id: 'a',
              text: 'git branch feature origin/feature',
              isCorrect: false,
              explanation: 'Неправильно. Это создаст ветку, но не установит отслеживание.',
            },
            {
              id: 'b',
              text: 'git checkout --track origin/feature',
              isCorrect: true,
              explanation:
                'Правильно! --track создаёт локальную ветку и устанавливает отслеживание.',
            },
            {
              id: 'c',
              text: 'git clone origin/feature',
              isCorrect: false,
              explanation: 'Неправильно. clone используется для клонирования репозитория.',
            },
            {
              id: 'd',
              text: 'git pull origin/feature',
              isCorrect: false,
              explanation: 'Неправильно. pull загружает изменения, но не создаёт ветку.',
            },
          ],
          explanation:
            'git checkout --track origin/feature создаёт локальную ветку feature, отслеживающую origin/feature. Также можно использовать git checkout -b feature origin/feature.',
          difficulty: 'medium',
        },
        {
          id: '5-3-q3',
          question: 'Какая команда удаляет устаревшие ссылки на удалённые ветки?',
          options: [
            {
              id: 'a',
              text: 'git branch --clean',
              isCorrect: false,
              explanation: 'Неправильно. Флага --clean нет.',
            },
            {
              id: 'b',
              text: 'git fetch --prune',
              isCorrect: true,
              explanation:
                'Правильно! --prune удаляет ссылки на ветки, которые больше не существуют на сервере.',
            },
            {
              id: 'c',
              text: 'git remote --cleanup',
              isCorrect: false,
              explanation: 'Неправильно. Флага --cleanup нет.',
            },
            {
              id: 'd',
              text: 'git branch --delete-remote',
              isCorrect: false,
              explanation: 'Неправильно. Флага --delete-remote нет.',
            },
          ],
          explanation:
            'git fetch --prune (или git remote prune origin) удаляет локальные ссылки на удалённые ветки, которые были удалены с сервера.',
          difficulty: 'medium',
        },
        {
          id: '5-3-q4',
          question: 'Можно ли напрямую коммитить в удалённую ветку (например, origin/main)?',
          options: [
            {
              id: 'a',
              text: 'Да, если есть права доступа',
              isCorrect: false,
              explanation: 'Неправильно. Удалённые ветки доступны только для чтения.',
            },
            {
              id: 'b',
              text: 'Нет, удалённые ветки доступны только для чтения',
              isCorrect: true,
              explanation:
                'Правильно! Удалённые ветки — это только для чтения. Изменения отправляются через push.',
            },
            {
              id: 'c',
              text: 'Да, если использовать --force',
              isCorrect: false,
              explanation:
                'Неправильно. --force не позволяет коммитить напрямую в удалённую ветку.',
            },
            {
              id: 'd',
              text: 'Только с правами администратора',
              isCorrect: false,
              explanation:
                'Неправильно. Даже администраторы не могут коммитить напрямую в удалённые ветки.',
            },
          ],
          explanation:
            'Удалённые ветки (origin/*) доступны только для чтения. Чтобы изменить их, нужно: 1) создать локальную ветку, 2) сделать коммиты, 3) отправить через git push.',
          difficulty: 'easy',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git remote',
          url: 'https://git-scm.com/docs/git-remote',
          type: 'documentation',
          description: 'Официальная документация о работе с remote',
        },
        {
          title: 'Tracking Branches',
          url: 'https://git-scm.com/book/en/v2/Git-Branching-Remote-Branches',
          type: 'book',
          description: 'Глава Pro Git об удалённых ветках',
        },
        {
          title: 'Managing Remote Repositories',
          url: 'https://www.atlassian.com/git/tutorials/syncing',
          type: 'article',
          description: 'Управление удалёнными репозиториями от Atlassian',
        },
      ],
    },
    {
      id: '5-4',
      moduleId: 'module-5',
      title: 'Pull Requests',
      slug: 'pull-requests',
      order: 4,
      theory: `# Pull Requests

Pull Request (PR) — это запрос на слияние изменений из одной ветки в другую.

## Что такое Pull Request?

PR — это способ предложить изменения:
1. Вы создаёте ветку с изменениями
2. Отправляете её в удалённый репозиторий
3. Создаёте PR через веб-интерфейс (GitHub, GitLab, Bitbucket)
4. Команда рассматривает и обсуждает изменения
5. После одобрения PR сливается

## Подготовка к PR

\`\`\`bash
# 1. Создать ветку функции
git checkout -b feature/amazing-feature

# 2. Внести изменения и закоммитить
git add .
git commit -m "Добавить удивительную функцию"

# 3. Отправить ветку
git push -u origin feature/amazing-feature
\`\`\`

## Хорошие практики PR

- **Маленькие PR**: Легче рассматривать и тестировать
- **Описательное название**: Чётко объясняет, что делает PR
- **Описание**: Контекст, скриншоты, инструкции по тестированию
- **Связанные issues**: Упоминайте связанные задачи

## Синхронизация с основной веткой

\`\`\`bash
# Обновить ветку функции с изменениями из main
git checkout feature/amazing-feature
git fetch origin
git rebase origin/main
git push --force-with-lease origin feature/amazing-feature
\`\`\`

## После слияния PR

\`\`\`bash
# Удалить локальную ветку
git branch -d feature/amazing-feature

# Удалить удалённую ветку
git push origin --delete feature/amazing-feature

# Обновить локальный main
git checkout main
git pull origin main
\`\`\`
`,
      steps: [
        {
          id: 1,
          title: 'Создание ветки для PR',
          description: 'Создайте новую ветку для функции.',
          expectedCommand: 'git checkout -b feature/my-feature',
          validationRegex: 'git (checkout|switch)\\s+-b\\s+feature/.*',
          hint: {
            level1: 'Создайте ветку для новой функции.',
            level2: 'Используйте git checkout -b с именем ветки.',
            level3: 'Введите: git checkout -b feature/my-feature',
          },
        },
        {
          id: 2,
          title: 'Проверка текущей ветки',
          description: 'Убедитесь, что вы на правильной ветке.',
          expectedCommand: 'git branch',
          hint: {
            level1: 'Проверьте текущую ветку.',
            level2: 'Используйте git branch.',
            level3: 'Введите: git branch',
          },
        },
      ],
      keyInsight: {
        main: 'Pull Requests — это не только код, но и обсуждение, ревью и документация изменений.',
        example:
          'Хороший PR: название "Добавить валидацию email", описание с мотивацией, скриншоты изменений, ссылки на issue. Ревьюеры оставляют комментарии, автор исправляет, затем merge.',
        warning:
          'Не создавайте огромные PR! Больше 400 строк кода сложно ревьюить. Разбивайте большие изменения на несколько маленьких PR.',
        tip: 'Используйте Draft PR для работы в процессе. Это показывает команде, что вы работаете над функцией, но ещё не готовы к ревью.',
        additional: [
          'PR должны быть атомарными — одна функция или исправление.',
          'Добавляйте тесты в том же PR, что и код.',
          'Связывайте PR с issues через ключевые слова: fixes #123.',
        ],
      },
      estimatedTime: 20,
      quizQuestions: [
        {
          id: '5-4-q1',
          question: 'Что такое Pull Request (PR)?',
          options: [
            {
              id: 'a',
              text: 'Команда Git для получения изменений',
              isCorrect: false,
              explanation: 'Неправильно. Для получения изменений используется git pull.',
            },
            {
              id: 'b',
              text: 'Запрос на слияние изменений из одной ветки в другую',
              isCorrect: true,
              explanation:
                'Правильно! PR — это предложение слить изменения, которое проходит ревью.',
            },
            {
              id: 'c',
              text: 'Автоматическое слияние веток',
              isCorrect: false,
              explanation: 'Неправильно. PR требует ревью и одобрения.',
            },
            {
              id: 'd',
              text: 'Способ удаления веток',
              isCorrect: false,
              explanation: 'Неправильно. PR не связан с удалением веток.',
            },
          ],
          explanation:
            'Pull Request — это запрос на слияние изменений. Он позволяет команде рассмотреть изменения, обсудить их и одобрить перед слиянием.',
          difficulty: 'easy',
        },
        {
          id: '5-4-q2',
          question: 'Какой размер PR считается оптимальным для ревью?',
          options: [
            {
              id: 'a',
              text: 'Чем больше, тем лучше',
              isCorrect: false,
              explanation: 'Неправильно. Большие PR сложно ревьюить.',
            },
            {
              id: 'b',
              text: 'До 400 строк кода',
              isCorrect: true,
              explanation: 'Правильно! PR до 400 строк легче рассматривать и тестировать.',
            },
            {
              id: 'c',
              text: 'Не менее 1000 строк',
              isCorrect: false,
              explanation: 'Неправильно. Такие PR очень сложно ревьюить.',
            },
            {
              id: 'd',
              text: 'Размер не важен',
              isCorrect: false,
              explanation: 'Неправильно. Размер PR важен для качества ревью.',
            },
          ],
          explanation:
            'Маленькие PR (до 400 строк) легче ревьюить, тестировать и откатывать в случае проблем. Большие PR следует разбивать на несколько маленьких.',
          difficulty: 'medium',
        },
        {
          id: '5-4-q3',
          question: 'Как обновить ветку PR изменениями из main?',
          options: [
            {
              id: 'a',
              text: 'git merge main',
              isCorrect: false,
              explanation: 'Неправильно. Лучше использовать rebase для чистой истории.',
            },
            {
              id: 'b',
              text: 'git rebase origin/main',
              isCorrect: true,
              explanation:
                'Правильно! Rebase применяет ваши коммиты поверх последних изменений main.',
            },
            {
              id: 'c',
              text: 'git pull main',
              isCorrect: false,
              explanation: 'Неправильно. Нужен fetch + rebase.',
            },
            {
              id: 'd',
              text: 'git sync main',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'git rebase origin/main применяет ваши коммиты поверх последних изменений из main. После этого нужно сделать git push --force-with-lease.',
          difficulty: 'medium',
        },
        {
          id: '5-4-q4',
          question: 'Для чего используется Draft PR?',
          options: [
            {
              id: 'a',
              text: 'Для удаления веток',
              isCorrect: false,
              explanation: 'Неправильно. Draft PR не связан с удалением.',
            },
            {
              id: 'b',
              text: 'Для обозначения работы в процессе, когда ещё не готовы к ревью',
              isCorrect: true,
              explanation:
                'Правильно! Draft PR показывает команде, что работа идёт, но ревью ещё не требуется.',
            },
            {
              id: 'c',
              text: 'Для автоматического слияния',
              isCorrect: false,
              explanation: 'Неправильно. Draft PR не сливается автоматически.',
            },
            {
              id: 'd',
              text: 'Для секретных изменений',
              isCorrect: false,
              explanation: 'Неправильно. Draft PR виден команде.',
            },
          ],
          explanation:
            'Draft PR используется для показа работы в процессе. Это полезно для ранней обратной связи или чтобы показать, что над функцией уже работают.',
          difficulty: 'easy',
        },
      ],
      resources: [
        {
          title: 'GitHub Docs: Pull Requests',
          url: 'https://docs.github.com/en/pull-requests',
          type: 'documentation',
          description: 'Документация GitHub о Pull Requests',
        },
        {
          title: 'Bitbucket Pull Requests',
          url: 'https://support.atlassian.com/bitbucket-cloud/docs/create-a-pull-request/',
          type: 'documentation',
          description: 'Документация Bitbucket о создании PR',
        },
        {
          title: 'Writing Great Pull Requests',
          url: 'https://www.pullrequest.com/blog/writing-a-great-pull-request-description/',
          type: 'article',
          description: 'Лучшие практики написания PR описаний',
        },
      ],
    },
    {
      id: '5-5',
      moduleId: 'module-5',
      title: 'SSH-подключение к Bitbucket',
      slug: 'ssh-bitbucket-setup',
      order: 5,
      theory: `# SSH-подключение к Bitbucket

SSH (Secure Shell) — протокол для безопасного подключения к удалённым серверам. Использование SSH для Git позволяет работать без постоянного ввода пароля.

## Почему SSH?

- **Безопасность**: Ключи сложнее взломать чем пароли
- **Удобство**: Не нужно вводить пароль при каждой операции
- **Автоматизация**: CI/CD пайплайны могут использовать деплой-ключи
- **Скорость**: Нет задержки на аутентификацию

## Типы SSH-ключей

| Тип | Размер | Безопасность | Скорость |
|-----|--------|--------------|----------|
| RSA | 4096 бит | Высокая | Средняя |
| Ed25519 | 256 бит | Очень высокая | Высокая |
| ECDSA | 521 бит | Высокая | Высокая |

**Рекомендация**: Используйте Ed25519 — современный и быстрый алгоритм.

## Шаг 1: Проверка существующих ключей

\`\`\`bash
# Проверить наличие SSH-ключей
ls -la ~/.ssh

# Типичные имена ключей:
# id_rsa / id_rsa.pub (RSA)
# id_ed25519 / id_ed25519.pub (Ed25519)
\`\`\`

## Шаг 2: Генерация SSH-ключа

\`\`\`bash
# Генерация Ed25519 ключа (рекомендуется)
ssh-keygen -t ed25519 -C "ваш-email@example.com"

# Генерация RSA ключа (для старых систем)
ssh-keygen -t rsa -b 4096 -C "ваш-email@example.com"

# Генерация ключа для конкретного сервиса
ssh-keygen -t ed25519 -C "ваш-email@example.com" -f ~/.ssh/bitbucket_key
\`\`\`

При генерации вас попросят:
1. **Местоположение**: Нажмите Enter для стандартного или укажите путь
2. **Пароль (passphrase)**: Рекомендуется установить для дополнительной защиты

## Шаг 3: Добавление ключа в SSH-агент

\`\`\`bash
# Запустить SSH-агент (если не запущен)
eval "$(ssh-agent -s)"

# Добавить ключ в агент
ssh-add ~/.ssh/id_ed25519

# Для macOS (добавить в Keychain)
ssh-add --apple-use-keychain ~/.ssh/id_ed25519
\`\`\`

## Шаг 4: Добавление ключа в Bitbucket

1. **Скопируйте публичный ключ**:
\`\`\`bash
# Вывести ключ в терминал
cat ~/.ssh/id_ed25519.pub

# Или скопировать сразу в буфер обмена (macOS)
pbcopy < ~/.ssh/id_ed25519.pub

# Linux (требуется xclip)
xclip -sel clip < ~/.ssh/id_ed25519.pub
\`\`\`

2. **Добавьте в Bitbucket**:
   - Откройте bitbucket.org и войдите в аккаунт
   - Нажмите на аватар → **Personal settings**
   - Выберите **SSH keys** в разделе SECURITY
   - Нажмите **Add key**
   - Вставьте публичный ключ и нажмите **Add key**

## Шаг 5: Проверка подключения

\`\`\`bash
# Проверить подключение к Bitbucket
ssh -T git@bitbucket.org

# Ожидаемый ответ:
# logged in as username.
# You can use Git to connect to Bitbucket. Shell access is disabled.
\`\`\`

## Настройка для нескольких аккаунтов

Если вы используете разные аккаунты для работы и личных проектов:

\`\`\`bash
# Создайте конфигурационный файл
nano ~/.ssh/config
\`\`\`

Добавьте содержимое:

\`\`\`
# Личный Bitbucket
Host bitbucket.org
  HostName bitbucket.org
  User git
  IdentityFile ~/.ssh/id_ed25519

# Рабочий Bitbucket
Host bitbucket-work
  HostName bitbucket.org
  User git
  IdentityFile ~/.ssh/bitbucket_work_key
\`\`\`

Использование:
\`\`\`bash
# Личный проект
git clone git@bitbucket.org:personal/project.git

# Рабочий проект
git clone git@bitbucket-work:company/project.git
\`\`\`

## Переключение с HTTPS на SSH

\`\`\`bash
# Проверить текущий remote
git remote -v

# Изменить remote на SSH
git remote set-url origin git@bitbucket.org:username/repo.git

# Проверить изменение
git remote -v
\`\`\`

## Устранение неполадок

\`\`\`bash
# Подробный вывод для диагностики
ssh -vT git@bitbucket.org

# Проверить, какой ключ используется
ssh -Tv git@bitbucket.org 2>&1 | grep "identity"

# Проверить права доступа к ключам
ls -la ~/.ssh
# Должны быть:
# id_ed25519 - 600 (только владелец может читать)
# id_ed25519.pub - 644 (все могут читать)

# Исправить права
chmod 600 ~/.ssh/id_ed25519
chmod 644 ~/.ssh/id_ed25519.pub
\`\`\`

## Deploy Keys для CI/CD

Для автоматических деплоев используйте Deploy Keys:

1. Генерируйте ключ без passphrase:
\`\`\`bash
ssh-keygen -t ed25519 -C "deploy@my-server" -f ~/.ssh/deploy_key -N ""
\`\`\`

2. Добавьте публичный ключ в **Repository settings → Access keys**

3. Используйте приватный ключ в CI/CD переменных
`,
      steps: [
        {
          id: 1,
          title: 'Проверка существующих SSH-ключей',
          description: 'Проверьте, есть ли у вас уже SSH-ключи.',
          expectedCommand: 'ls -la ~/.ssh',
          hint: {
            level1: 'Вам нужно посмотреть содержимое директории SSH.',
            level2: 'Используйте ls -la для просмотра файлов в ~/.ssh.',
            level3: 'Введите: ls -la ~/.ssh',
          },
        },
        {
          id: 2,
          title: 'Проверка подключения к Bitbucket',
          description: 'Проверьте, можете ли вы подключиться к Bitbucket по SSH.',
          expectedCommand: 'ssh -T git@bitbucket.org',
          hint: {
            level1: 'Попробуйте подключиться к Bitbucket через SSH.',
            level2: 'Используйте ssh -T с адресом Bitbucket.',
            level3: 'Введите: ssh -T git@bitbucket.org',
          },
        },
      ],
      keyInsight: {
        main: 'SSH-ключи обеспечивают безопасную и удобную аутентификацию без ввода пароля при каждой операции.',
        example:
          'После настройки SSH вы можете клонировать репозитории командой git clone git@bitbucket.org:user/repo.git без ввода пароля. Это особенно удобно при частых push/pull операциях.',
        warning:
          'Никогда не передавайте приватный ключ (~/.ssh/id_ed25519 без .pub) другим людям! Публичный ключ (с расширением .pub) можно безопасно распространять.',
        tip: 'Для максимальной безопасности установите passphrase на SSH-ключ и используйте ssh-agent для кэширования пароля на время сессии.',
        additional: [
          'Ed25519 — современный алгоритм, который быстрее и безопаснее RSA.',
          'Passphrase на ключе защищает даже если файл ключа украдут.',
          'Для CI/CD используйте Deploy Keys с правами только на чтение.',
          'SSH-агент автоматически загружает ключи при входе в систему.',
        ],
      },
      estimatedTime: 25,
      quizQuestions: [
        {
          id: '5-5-q1',
          question: 'Какой тип SSH-ключа рекомендуется использовать?',
          options: [
            {
              id: 'a',
              text: 'RSA 1024 бит',
              isCorrect: false,
              explanation: 'Неправильно. RSA 1024 бит считается небезопасным.',
            },
            {
              id: 'b',
              text: 'Ed25519',
              isCorrect: true,
              explanation: 'Правильно! Ed25519 — современный, быстрый и безопасный алгоритм.',
            },
            {
              id: 'c',
              text: 'DSA',
              isCorrect: false,
              explanation: 'Неправильно. DSA устарел и не рекомендуется.',
            },
            {
              id: 'd',
              text: 'RSA без ограничения размера',
              isCorrect: false,
              explanation: 'Неправильно. RSA медленнее Ed25519.',
            },
          ],
          explanation:
            'Ed25519 — современный алгоритм на основе эллиптических кривых. Он быстрее и безопаснее RSA при меньшем размере ключа (256 бит против 4096 бит).',
          difficulty: 'easy',
        },
        {
          id: '5-5-q2',
          question: 'Какой файл SSH-ключа можно безопасно передавать другим?',
          options: [
            {
              id: 'a',
              text: 'id_ed25519 (приватный ключ)',
              isCorrect: false,
              explanation: 'Неправильно. Приватный ключ никогда нельзя передавать!',
            },
            {
              id: 'b',
              text: 'id_ed25519.pub (публичный ключ)',
              isCorrect: true,
              explanation: 'Правильно! Публичный ключ (.pub) можно безопасно распространять.',
            },
            {
              id: 'c',
              text: 'Оба файла',
              isCorrect: false,
              explanation: 'Неправильно. Приватный ключ нельзя передавать.',
            },
            {
              id: 'd',
              text: 'Ни один из файлов',
              isCorrect: false,
              explanation: 'Неправильно. Публичный ключ можно передавать.',
            },
          ],
          explanation:
            'Публичный ключ (с расширением .pub) предназначен для распространения. Приватный ключ (без .pub) должен храниться в секрете.',
          difficulty: 'easy',
        },
        {
          id: '5-5-q3',
          question: 'Для чего используется passphrase в SSH-ключе?',
          options: [
            {
              id: 'a',
              text: 'Для ускорения подключения',
              isCorrect: false,
              explanation: 'Неправильно. Passphrase не влияет на скорость.',
            },
            {
              id: 'b',
              text: 'Для дополнительной защиты приватного ключа',
              isCorrect: true,
              explanation: 'Правильно! Passphrase шифрует приватный ключ.',
            },
            {
              id: 'c',
              text: 'Для автоматического входа',
              isCorrect: false,
              explanation: 'Неправильно. Passphrase требует ввода при каждом использовании.',
            },
            {
              id: 'd',
              text: 'Для генерации публичного ключа',
              isCorrect: false,
              explanation: 'Неправильно. Passphrase не связан с генерацией публичного ключа.',
            },
          ],
          explanation:
            'Passphrase — это пароль, который шифрует приватный ключ. Даже если файл ключа украдут, без passphrase им нельзя воспользоваться.',
          difficulty: 'medium',
        },
        {
          id: '5-5-q4',
          question: 'Какая команда проверяет подключение SSH к Bitbucket?',
          options: [
            {
              id: 'a',
              text: 'ssh bitbucket.org',
              isCorrect: false,
              explanation: 'Неправильно. Нужен флаг -T и правильный пользователь.',
            },
            {
              id: 'b',
              text: 'ssh -T git@bitbucket.org',
              isCorrect: true,
              explanation:
                'Правильно! -T отключает выделение терминала, git@ — пользователь для Git.',
            },
            {
              id: 'c',
              text: 'ping bitbucket.org',
              isCorrect: false,
              explanation: 'Неправильно. ping проверяет сетевое соединение, не SSH.',
            },
            {
              id: 'd',
              text: 'ssh-check bitbucket.org',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'ssh -T git@bitbucket.org проверяет SSH-подключение к Bitbucket. Флаг -T отключает терминал, git@ — это пользователь для Git-операций.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'Bitbucket SSH Documentation',
          url: 'https://support.atlassian.com/bitbucket-cloud/docs/set-up-an-ssh-key/',
          type: 'documentation',
        },
        {
          title: 'SSH Keys for GitHub, GitLab, Bitbucket',
          url: 'https://docs.github.com/en/authentication/connecting-to-github-with-ssh',
          type: 'documentation',
        },
        {
          title: 'Secure Shell Wikipedia',
          url: 'https://en.wikipedia.org/wiki/Secure_Shell',
          type: 'article',
        },
      ],
    },
  ],
};

export { module5 };
